import api from 'api';
import { getBuyingPortfolio } from 'utils';
import { Listing } from 'models/Listing';
import { NotificationType, USER_TYPE_NOTIFICATION } from 'models/Notification';
import DocumentViewContext from 'providers/DocumentView/DocumentView.context';
import { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Q_AND_A_SIDES } from 'router/subrouters/DataRoom/models/QAndA';

export type NotificationAction = {
  notification: NotificationType;
};

const getRouteToBuyerDealAndOpenFile = (listing: Listing) => {
  const { listingEnquiryId } = listing || {};
  return getBuyingPortfolio(listingEnquiryId, 'listing-information');
};

export default function useAction() {
  const { openNode } = useContext(DocumentViewContext);

  const navigate = useNavigate();

  const handleOpenNode = useCallback(
    ({ notification }: NotificationAction) => {
      if (notification.content.dataRoomNode) {
        openNode(notification.content.dataRoomNode, {
          dataRoomName:
            notification.content.listing?.dataRoomName ||
            notification.content.dealRoom?.name,
          ownerId:
            notification.content.dealRoom?.userId ||
            notification.content.listing?.userId,
          userSide:
            notification.userType === USER_TYPE_NOTIFICATION.BUYER
              ? Q_AND_A_SIDES.BUY
              : Q_AND_A_SIDES.SELL,
          dealRoomId:
            notification.content.dealRoom?._id ||
            notification.content.listing?.dealRoomId,
        });
      } else {
        navigate(getRouteToBuyerDealAndOpenFile(notification.content.listing));
      }
    },
    [navigate, openNode],
  );

  const NOTIFICATION_PRIMARY_ACTION = useMemo(
    () =>
      ({
        42: ({ notification }) =>
          api.nudges.confirmInterest(notification.content.nudgeNotificationId),
      } as Record<number, (config: NotificationAction) => void>),
    [],
  );

  const NOTIFICATION_SECONDARY_ACTION = useMemo(
    () =>
      ({
        23: handleOpenNode,
        24: handleOpenNode,
        42: ({ notification }) =>
          api.portfolio.buyer.withdrawInterest(notification.content.leadId),
        53: handleOpenNode,
      } as Record<number, (config: NotificationAction) => void>),
    [handleOpenNode],
  );

  const getPrimaryAction = useCallback(
    (config: NotificationAction) =>
      NOTIFICATION_PRIMARY_ACTION[config.notification.typeCode],
    [NOTIFICATION_PRIMARY_ACTION],
  );

  const getSecondaryAction = useCallback(
    (config: NotificationAction) =>
      NOTIFICATION_SECONDARY_ACTION[config.notification.typeCode],
    [NOTIFICATION_SECONDARY_ACTION],
  );

  const getAction = useCallback(
    (notification: NotificationType) =>
      getPrimaryAction({ notification }) ||
      getSecondaryAction({ notification }),
    [getPrimaryAction, getSecondaryAction],
  );

  const getActionByType = useCallback(
    (
      notification: NotificationType,
      type: 'primary' | 'secondary' = 'primary',
    ) =>
      type === 'primary'
        ? getPrimaryAction({ notification })
        : getSecondaryAction({ notification }),
    [getPrimaryAction, getSecondaryAction],
  );

  return { getAction, getActionByType };
}
